import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Globe, ArrowLeftRight, CheckCircle2, ArrowRight, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const extensions = [
  { ext: ".com", price: "149 ₺" },
  { ext: ".net", price: "169 ₺" },
  { ext: ".com.tr", price: "89 ₺" },
  { ext: ".org", price: "179 ₺" },
  { ext: ".io", price: "1.299 ₺" },
  { ext: ".dev", price: "399 ₺" },
  { ext: ".app", price: "349 ₺" },
  { ext: ".ai", price: "2.499 ₺" },
];

const services = [
  {
    icon: Search,
    title: "Alan Adı Sorgulama",
    description: "500'den fazla uzantıda istediğiniz alan adının müsaitliğini anında kontrol edin.",
    bullets: ["Anlık sorgulama", "500+ uzantı", "Toplu sorgulama"],
  },
  {
    icon: Globe,
    title: "Alan Adı Satın Alma",
    description: "Güvenli ödeme ile saniyeler içinde alan adınızı kaydedin. WHOIS gizliliği ilk yıl ücretsiz.",
    bullets: ["Otomatik DNS", "Ücretsiz WHOIS gizliliği", "Çoklu ödeme yöntemi"],
  },
  {
    icon: ArrowLeftRight,
    title: "Alan Adı Transferi",
    description: "Mevcut alan adınızı kesintisiz biçimde DR SOFT'a taşıyın — ek yıl hediye.",
    bullets: ["Sıfır kesinti", "1 yıl ek süre hediye", "7/24 transfer desteği"],
  },
];

export default function Domains() {
  const [query, setQuery] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error("Lütfen bir alan adı yazın.");
      return;
    }
    toast.success(`"${query}" için arama başlatıldı`, {
      description: "Uzantı listesi birkaç saniye içinde yüklenecek (demo).",
    });
  };

  return (
    <div data-testid="domains-page">
      {/* HERO + search */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-50 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-96 radial-glow-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20 lg:pt-24 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-5">Alan Adı</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Mükemmel alan adını <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">hemen bul.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-xl mx-auto">
            500+ uzantıda uygun fiyatlı kayıt, ücretsiz WHOIS gizliliği ve hızlı transfer.
          </p>

          <form onSubmit={handleSearch} data-testid="domain-search-form" className="mt-10 max-w-2xl mx-auto">
            <div className="glass rounded-full p-2 flex items-center gap-2">
              <div className="pl-4 text-blue-400"><Search size={18} /></div>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="domain-search-input"
                placeholder="sirketiniz.com"
                className="flex-1 bg-transparent border-0 focus-visible:ring-0 text-base h-12 placeholder:text-zinc-500"
              />
              <button data-testid="domain-search-submit" type="submit" className="btn-shine rounded-full bg-blue-600 hover:bg-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(0,122,255,0.35)] transition-all">
                Sorgula
              </button>
            </div>
          </form>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {extensions.map((e) => (
              <div key={e.ext} data-testid={`ext-${e.ext.replace('.', '')}`} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm">
                <span className="font-semibold text-white">{e.ext}</span>
                <span className="text-zinc-500">{e.price}/yıl</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Hizmetler</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Alan adı, sadeleştirilmiş.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div key={s.title} data-testid={`domain-service-${i}`} className="card-hover glass rounded-3xl p-8 flex flex-col">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-700/10 border border-blue-500/20 flex items-center justify-center mb-6">
                  <s.icon size={24} strokeWidth={1.6} className="text-blue-400" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-3">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6 flex-1">{s.description}</p>
                <ul className="space-y-2 mb-6">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm text-zinc-300">
                      <CheckCircle2 size={15} className="text-blue-500 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY CTA */}
      <section className="relative py-20 bg-[#07080C]/40 border-y border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="glass rounded-3xl p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <Shield size={28} className="text-blue-400 mb-4" />
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
                Alan adınızı koruyun
              </h3>
              <p className="text-zinc-400 max-w-xl">
                DNSSEC, domain lock ve WHOIS gizliliği — tüm alan adlarımıza dahildir.
              </p>
            </div>
            <Link to="/guvenlik" data-testid="domain-security-cta" className="btn-shine inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-4 text-sm font-semibold text-white whitespace-nowrap transition-all">
              Güvenliği incele <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
